import type React from "react";
import { Navigate } from "react-router-dom";
import GlobalLoader from "@/components/common/BrandLogo";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/lib/auth";
import AttorneyOverview from "@/pages/overview/AttorneyOverview";
import ClientOverview from "@/pages/overview/ClientOverview";
import ExternalDeals from "@/pages/overview/ExternalDeals";
import LenderOverview from "@/pages/overview/LenderOverview";
import Overview from "@/pages/overview/Overview";
import { PUBLIC_ROUTES } from "@/routes/url";

import type { DealParticipation } from "@/types/authentication.types";

const externalOverviewByRole: Record<
  string,
  ((props: { participation?: DealParticipation | null }) => React.JSX.Element) | undefined
> = {
  client: ClientOverview,
  lender: LenderOverview,
  attorney: AttorneyOverview,
};

export default function RoleOverview() {
  const { user, context, loading } = useAuth();

  if (loading) return <GlobalLoader />;
  if (!user) return <Navigate to={PUBLIC_ROUTES.LOGIN} replace />;

  if (context?.membership) {
    return (
      <DashboardLayout>
        <Overview />
      </DashboardLayout>
    );
  }

  const participations = context?.deal_participations || [];

  // More than one room and there is no defensible "first" one to open. This
  // used to pick `participations[0]` regardless, so a client selling one house
  // and buying another could only ever reach whichever invitation they happened
  // to accept first — the second deal had no link to it anywhere in the app.
  if (participations.length > 1) {
    return (
      <DashboardLayout>
        <ExternalDeals />
      </DashboardLayout>
    );
  }

  const participation = participations.find(
    (item) => externalOverviewByRole[item.participant_role],
  );
  const ExternalOverview = participation
    ? externalOverviewByRole[participation.participant_role]
    : null;

  if (ExternalOverview) {
    return (
      <DashboardLayout>
        <ExternalOverview participation={participation} />
      </DashboardLayout>
    );
  }
  return <Navigate to={context?.landing_route || PUBLIC_ROUTES.LANDING} replace />;
}
